import {FC, useEffect, useState} from 'react';
import {
	ContentHeader,
	messageBoxError,
	messageBoxSuccess,
	ReactableColumnDef,
	ReactableWithClientSidePagination
} from '@deviceinsight/ng-ui-components';
import {Button, ConfirmProps, Modal, SpinnerContainer, withConfirm} from '@deviceinsight/ng-ui-basic-components';
import CustomerSelect from './CustomerSelect';
import {listSubtenants} from '../../api/subtenantsApi';
import {fetchAllAssetGroupsOfUser} from '../../api/assetGroupsApi';
import {Subtenant} from '../../modules/subtenants/types';
import {KisApiUser} from '../../modules/kisapi/types';
import hasAuthority from '../common/hasAuthority';
import {getInitialPaginationSettings} from '../../components/common/paginationHelpers';
import {createKisApiUserWithDescr, deleteKisApiUser, listKisApiUsers, regenerateApiKey} from '../../api/kisCustomApi';
import {Trans, useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';

import ApiKeyComponent from './columnComponents/ApiKeyComponent';
import ClientIdComponent from './columnComponents/ClientIdComponent';

import {AUTHORITY_READ_SUBTENANT, AUTHORITY_WRITE_KIS_API_USER} from '../../modules/common/consts';

import './KisApiCustomPage.less';

type Props = ConfirmProps;

type Item = {
	type: 'item';
	value: string;
	label: string;
};

const KisApiCustomPage: FC<Props> = ({confirm}) => {
	const [loading, setLoading] = useState(true);
	const [customers, setCustomers] = useState<Subtenant[]>([]);
	const [selectedCustomerID, setSelectedCustomerID] = useState<string>();
	const [kisApiUsers, setKisApiUsers] = useState<KisApiUser[]>([]);
	const [kisApiEnabled, setKisApiEnabled] = useState<boolean>(true);
	const [maxLimitOfUsersReached, setMaxLimitOfUsersReached] = useState<boolean>(false);
	const [createModalIsOpen, setCreateModalIsOpen] = useState<boolean>(false);
	const [description, setDescription] = useState<string>();

	const {t} = useTranslation();

	const canReadSubtenants = hasAuthority(AUTHORITY_READ_SUBTENANT);
	const canWriteKisApiUser = hasAuthority(AUTHORITY_WRITE_KIS_API_USER);

	const GENERATE_API_KEY_CONFIRMATION_TITLE = {
		id: 'kisapi.credentials.generateApiKeyConfirmTitle',
		defaultValue: 'Generate new API Key'
	};
	const GENERATE_API_KEY_CONFIRMATION_BODY = {
		id: 'kisapi.credentials.generateApiKeyConfirmBody',
		defaultValue:
			'The current api key will be invalidated if you’re generating a new api key. Do you want to proceed?'
	};

	const DELETE_API_KEY_CONFIRMATION_TITLE = {
		id: 'kisapi.credentials.deleteApiKeyConfirmTitle',
		defaultValue: 'Delete API Key'
	};
	const DELETE_API_KEY_CONFIRMATION_BODY = {
		id: 'kisapi.credentials.deleteApiKeyConfirmBody',
		defaultValue: 'Really delete API key?'
	};

	const linkToDocumentation = t('kisapi.documentation.link', {
		defaultValue: 'https://docs.kisme.com'
	});
	const linkToDocumentationDisplayName = t('kisapi.documentation.displayName', {
		defaultValue: 'Check out the the KIS.API Documentation here'
	});

	useEffect(() => {
		if (canReadSubtenants) {
			setLoading(true);
			listSubtenants()
				.then((customers) => {
					setCustomers(customers.content);
				})
				.finally(() => setLoading(false));
		} else {
			setLoading(true);
			fetchAllAssetGroupsOfUser()
				.then((customers) => {
					setSelectedCustomerID(customers.content[0].properties.subtenantId as string);
				})
				.finally(() => setLoading(false));
		}
	}, [canReadSubtenants]);

	useEffect(() => {
		if (selectedCustomerID) {
			setLoading(true);
			listKisApiUsers(selectedCustomerID)
				.then((users) => {
					setKisApiUsers(users);
					setKisApiEnabled(true);
					if (kisApiUsers.length === 10) {
						setMaxLimitOfUsersReached(true);
					}
				})
				.catch((error) => {
					if (error?.response?.status === 400) {
						setKisApiEnabled(false);
					}
				})
				.finally(() => setLoading(false));
		}
	}, [kisApiUsers.length, selectedCustomerID]);

	const onSelectedItem = (i: Item | null) => {
		setSelectedCustomerID(i ? i.value : undefined);
	};

	const reloadKisApiUsers = () => {
		if (selectedCustomerID) {
			setLoading(true);
			listKisApiUsers(selectedCustomerID)
				.then((users) => {
					setKisApiUsers(users);
					setKisApiEnabled(true);
					if (kisApiUsers.length === 10) {
						setMaxLimitOfUsersReached(true);
					}
				})
				.catch((error) => {
					if (error?.response?.status === 400) {
						setKisApiEnabled(false);
					}
				})
				.finally(() => setLoading(false));
		}
	};

	const createKisApiUser = () => {
		if (selectedCustomerID && description) {
			const descrObj = {
				description: description
			};
			setLoading(true);
			createKisApiUserWithDescr(selectedCustomerID, descrObj)
				.catch(() =>
					messageBoxError(
						<Trans i18nKey="kisapi.createUser.couldntCreateUserMsg">
							KIS.API User could not be created!
						</Trans>
					)
				)
				.then(() => {
					setCreateModalIsOpen(false);
					setDescription('');
					messageBoxSuccess(
						<Trans i18nKey="kisapi.createUser.userCreationSuccess">KIS.API user was created</Trans>
					);
					reloadKisApiUsers();
				})
				.finally(() => setLoading(false));
		} else {
			messageBoxError(
				<Trans i18nKey="kisapi.createUser.couldntCreateUserMsg">KIS.API User could not be created!</Trans>
			);
		}
	};

	const generateApiKey = (clientId: string) => {
		if (selectedCustomerID) {
			confirm(GENERATE_API_KEY_CONFIRMATION_TITLE, GENERATE_API_KEY_CONFIRMATION_BODY).then(() => {
				setLoading(true);
				regenerateApiKey(selectedCustomerID, clientId)
					.catch(() =>
						messageBoxError(
							<Trans i18nKey="kisapi.createUser.apiKeyGenerationError">
								KIS.API key was not regenerated!
							</Trans>
						)
					)
					.then(() => {
						messageBoxSuccess(
							<Trans i18nKey="kisapi.createUser.apiKeyGenerationSuccess">
								KIS.API key was regenerated
							</Trans>
						);
						reloadKisApiUsers();
					})
					.finally(() => setLoading(false));
			});
		}
	};

	const deleteApiKey = (clientId: string) => {
		if (selectedCustomerID) {
			confirm(DELETE_API_KEY_CONFIRMATION_TITLE, DELETE_API_KEY_CONFIRMATION_BODY).then(() => {
				setLoading(true);
				deleteKisApiUser(selectedCustomerID, clientId)
					.catch(() =>
						messageBoxError(
							<Trans i18nKey="kisapi.createUser.userDeletionError">KIS.API user was not deleted!</Trans>
						)
					)
					.then(() => {
						messageBoxSuccess(
							<Trans i18nKey="kisapi.createUser.userDeletionSuccess">KIS.API user was deleted</Trans>
						);
						reloadKisApiUsers();
					})
					.finally(() => setLoading(false));
			});
		}
	};

	const columnDefinitions = (): Array<ReactableColumnDef<KisApiUser>> => {
		const columnDefs = [
			{
				name: 'description',
				label: {
					id: 'kisapi.columns.description',
					defaultValue: 'Description'
				},
				width: '25%',
				sortable: true,
				render: (element: KisApiUser) => {
					return <span data-testid={`description-${element.clientId}`}>{element.description}</span>;
				}
			},
			{
				name: 'clientId',
				label: {
					id: 'kisapi.columns.clientId',
					defaultValue: 'Client ID'
				},
				width: '25%',
				sortable: true,
				render: (element: KisApiUser) => {
					return <ClientIdComponent clientId={element.clientId}></ClientIdComponent>;
				}
			},
			{
				name: 'apiKey',
				label: {
					id: 'kisapi.columns.apiKey',
					defaultValue: 'API Key'
				},
				width: '25%',
				sortable: true,
				render: (element: KisApiUser) => {
					return <ApiKeyComponent apiKey={element.apiKey}></ApiKeyComponent>;
				}
			},
			{
				name: 'regenerateApiKey',
				label: '',
				width: '12.5%',
				sortable: false,
				render: (element: KisApiUser) => {
					return (
						<span data-testid="regenerateApiKey">
							{canWriteKisApiUser && (
								<Button
									primary
									type="submit"
									disabled={false}
									onClick={() => generateApiKey(element.clientId)}>
									<Trans i18nKey="kisapi.columns.regenerateApiKey">Regenerate API Key</Trans>
								</Button>
							)}
						</span>
					);
				}
			},
			{
				name: 'deleteApiKey',
				label: '',
				width: '12.5%',
				sortable: false,
				render: (element: KisApiUser) => {
					return (
						<span data-testid="deleteApiKey">
							{canWriteKisApiUser && (
								<Button
									danger
									type="submit"
									disabled={false}
									onClick={() => deleteApiKey(element.clientId)}>
									<Trans i18nKey="kisapi.columns.deleteApiKey">Delete API Key</Trans>
								</Button>
							)}
						</span>
					);
				}
			}
		];
		return columnDefs;
	};

	return (
		<>
			<div style={{marginTop: 30}}>
				{createModalIsOpen && (
					<Modal
						header={<Trans i18nKey="kisapi.info.createUser">Create KIS.API User</Trans>}
						content={
							<div className="field Customer-Id">
								<label>
									<Trans i18nKey="kisapi.createUser.description">Description:</Trans>
								</label>
								<input
									type="text"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									size={64}
									maxLength={64}
									data-testid="createUser.description"
									required
								/>
							</div>
						}
						footer={
							<>
								<Button primary onClick={createKisApiUser} disabled={!description}>
									<Trans i18nKey="kisapi.createUser.createUserButton">Create</Trans>
								</Button>
								<Button
									primary
									onClick={() => {
										setCreateModalIsOpen(false);
										setDescription('');
									}}>
									<Trans i18nKey="kisapi.createUser.abortCreationButton">Abort</Trans>
								</Button>
							</>
						}
					/>
				)}
				{canWriteKisApiUser && !maxLimitOfUsersReached && selectedCustomerID && kisApiEnabled && (
					<ContentHeader title={''}>
						<div className="di header-toolbar">
							<Button
								className="tool add"
								style={{border: 0}}
								onClick={() => {
									setCreateModalIsOpen(true);
								}}
								data-testid="addUserButton"
							/>
						</div>
					</ContentHeader>
				)}
				{canReadSubtenants && (
					<CustomerSelect loading={loading} customers={customers} onSelectedItem={onSelectedItem} />
				)}
				{!canReadSubtenants && <div className="customer-select"></div>}
				{!kisApiEnabled && <Trans i18nKey="kisapi.info.notEnabled">KIS.API feature is not enabled</Trans>}
				{kisApiEnabled && selectedCustomerID && (
					<div className="kis-api-credentials">
						<h2 className="di header dividing">
							<Trans i18nKey="kisapi.credentials.title">KIS.API Credentials</Trans>
						</h2>
						<SpinnerContainer show={loading}>
							<div style={{marginTop: 30}}>
								<ReactableWithClientSidePagination
									columnDefs={columnDefinitions()}
									isLoading={loading}
									items={kisApiUsers}
									uniqueItemKey={(kisApiUser: KisApiUser) => kisApiUser.clientId}
									paginationSettings={getInitialPaginationSettings(
										'description',
										'ASC'
									)}></ReactableWithClientSidePagination>
							</div>
						</SpinnerContainer>

						<h2 className="di header dividing">
							<Trans i18nKey="kisapi.credentials.documentation">Documentation</Trans>
						</h2>
						<div className="kis-api-credentials field docu">
							<label>
								<a href={linkToDocumentation} target="_blank" rel="noopener noreferrer">
									{linkToDocumentationDisplayName}
								</a>
							</label>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default withConfirm(KisApiCustomPage);
